import {AppParams, AppArgs} from './types.ts'

import {createApp} from 'vue'

import { createPinia } from 'pinia'
import { useGlobalStore } from './stores/global'

import './css/style.css'
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import PrimeVue from 'primevue/config';
import theme_options from './js/primevue_theme.ts';
import ToastService from 'primevue/toastservice';

import TypeIt from './components/TypeIt.vue'
import FranchiseList from './apps/franchise/list.vue'
import SpecialistList from './apps/specialist/list.vue'
import HeaderAccount from './apps/HeaderAccount.vue'
import MailPdf from './apps/MailPdf.vue'
import ReactionForm from './apps/ReactionForm.vue'
import SearchServiceForm from './apps/search_service/form.vue';
import SignUpForm from './apps/sign_up/form.vue'
import ListingList from './apps/listing/list.vue'
import ListingNew from './apps/listing/new.vue'
import SearchBar from './apps/search_bar/search_bar.vue'
import ValuationStep3 from './apps/valuation/step3.vue'
import QuickLogin from './apps/QuickLogin.vue'

import initMenu from './js/menu.ts'
import initDialog from './js/dialog.ts'
import initFAQ from './js/faq.ts'
import submitForm from './js/submit_form.ts'
import initAjaxForm from './js/ajax_form.ts'
import toggleFavorite from './js/toggle_favorite.ts'
import initSwiper from './js/swiper.ts'
import initGallery from './js/gallery.ts'
import initFloatingButton from './js/floating_button.ts'
import initMoreToggle from './js/more_toggle.ts'

declare global {
    interface Window {
        App: object; // Add the 'App' object to the global 'window' scope
    }
}

window.App = (() => {
    const run = (params: AppParams, args: AppArgs[]) => {
        initMenu();
        initMoreToggle();

        // Initialize the global store. Pinia needs an app instance for it to be initialized
        const mainApp = createApp({});
        const pinia = createPinia();
        mainApp.use(pinia);
        // Now we can use the Pinia global store
        const globalStore = useGlobalStore();
        globalStore.$patch(params);

        const executeCase = (arg: AppArgs) => {
            switch (arg.what) {
                case 'typeit':
                    createApp(TypeIt, arg.options)
                        .use(PrimeVue, theme_options)
                        .mount(arg.el);
                    break;
                case 'dialog':
                    initDialog(arg.el);
                    break;
                case 'faq':
                    initFAQ(arg.el);
                    break;
                case 'search_service_form':
                    createApp(SearchServiceForm, arg.options)
                        .use(PrimeVue, theme_options)
                        .mount(arg.el);
                    break;
                case 'search_bar':
                    createApp(SearchBar, arg.options)
                        .use(PrimeVue, theme_options)
                        .mount(arg.el);
                    break;
                case 'specialist':
                    createApp(SpecialistList, {
                        categoryOptions: arg.options.categories,
                        trans: arg.options.trans,
                    })
                        .use(PrimeVue, theme_options)
                        .mount(arg.el);
                    break;
                case 'franchise':
                    createApp(FranchiseList, {
                        sectorOptions: arg.options.sectors,
                        regionOptions: arg.options.regions,
                        trans: arg.options.trans,
                    })
                        .use(PrimeVue, theme_options)
                        .mount(arg.el);
                    break;
                case 'dialog_ajax_form':
                    initDialog(arg.el, () => {
                        initAjaxForm(arg.options.form, arg.options.url);
                    });
                    break;
                case 'mail_pdf':
                    initDialog(arg.el, () => {
                        const app = createApp(MailPdf, {
                            post_url: arg.options.url,
                            trans: arg.options.trans,
                        });
                        app.mount(arg.el + '-form');

                        return app;
                    });
                    break;
                case 'sign_up_form':
                    createApp(SignUpForm, {
                        verifyEmailUrl: arg.options.verifyEmailUrl,
                        saveProfileUrl: arg.options.saveProfileUrl,
                        accountUrl: arg.options.accountUrl,
                        trans: arg.options.trans,
                    })
                        .mount(arg.el);
                    break;
                case 'submit_form':
                    submitForm(arg.el, arg.options.action);
                    break;
                case 'reaction_form':
                    initDialog(arg.el, () => {
                        const app = createApp(ReactionForm, {
                            needs_premium: arg.options.needs_premium,
                            post_url: arg.options.url,
                            verify_email_url: arg.options.verify_email_url,
                            forgot_password_url: arg.options.forgot_password_url,
                            login_url: arg.options.login_url,
                            result_url: arg.options.result_url,
                            ask_meta: arg.options.ask_meta,
                            trans: arg.options.trans,
                            select_options: arg.options.select_options,
                        });
                        app.use(PrimeVue, theme_options);
                        app.mount(arg.options.el);

                        return app;
                    });
                    break;
                case 'header_account':
                    createApp(HeaderAccount, arg.options).mount(arg.el)
                    break;
                case 'toggle_favorite':
                    arg.options.quick_register_url += `?type=favorite&id=${arg.options.id}`;
                    toggleFavorite(arg.el, arg.options, () => {
                        initDialog(arg.el, () => {
                            const app = createApp(MailPdf, {
                                post_url: arg.options.quick_register_url,
                                trans: arg.options.trans,
                            });
                            app.mount(arg.options.dialog_el + '-form');

                            return app;
                        });
                    });
                    break;
                case 'swiper':
                    initSwiper(arg.el);
                    break;
                case 'gallery':
                    initGallery(arg.el);
                    break;
                case 'listing':
                    createApp(ListingList, {
                        postUrl: arg.options.postUrl,
                        sectorOptions: arg.options.sectors,
                        regionOptions: arg.options.regions,
                        purposeOptions: arg.options.purposes,
                        turnoverOptions: arg.options.turnovers,
                        typeOfAcquisitionOptions: arg.options.typeOfAcquisition,
                        typeOfTransactionOptions: arg.options.typeOfTransaction,
                        lifePhaseEnterpriseOptions: arg.options.lifePhaseEnterprise,
                        legalEntityOptions: arg.options.legalEntity,
                        earningBeforeTaxesOptions: arg.options.earningBeforeTaxes,
                        employeesInFTEOptions: arg.options.employeesInFTE,
                        askingPriceOptions: arg.options.askingPrice,
                        platformListingOptions: arg.options.platformListing,
                        purposeType: arg.options.purposeType,
                        archive: arg.options.archive,
                        trans: arg.options.trans,
                    })
                        .use(PrimeVue, theme_options)
                        .mount(arg.el);
                    break;
                case 'listing_new':
                    createApp(ListingNew, arg.options)
                        .use(PrimeVue, theme_options)
                        .use(ToastService)
                        .mount(arg.el)
                    break;
                case 'valuation_step3':
                    createApp(ValuationStep3, arg.options)
                        .use(PrimeVue, theme_options)
                        .mount(arg.el);
                    break;
                case 'floating_button':
                    initFloatingButton(arg.el);
                    break;
                case 'quick_login':
                    arg.options.callback = () => {
                        document.location.reload();
                    }
                    createApp(QuickLogin, arg.options)
                        .mount(arg.el);
                    break;
                default:
                    console.error(`Unknown app type: "${arg.what}" on element: ${arg.el} in main.ts`);
                    break;
            }
        };

        const userInteractionHandler = (args: AppArgs[]) => {
            const handler = () => {
                for (const arg of args) {
                    executeCase(arg);
                }
                window.removeEventListener('click', handler);
                window.removeEventListener('keydown', handler);
                window.removeEventListener('mousemove', handler);
                window.removeEventListener('scroll', handler);
            };

            window.addEventListener('click', handler, {once: true});
            window.addEventListener('keydown', handler, {once: true});
            window.addEventListener('mousemove', handler, {once: true});
            window.addEventListener('scroll', handler, {once: true});
        };

        let delayed: AppArgs[] = [];
        for (const arg of args) {
            if (arg.options?.delay_execution ?? false) {
                delayed.push(arg)
            } else {
                executeCase(arg);
            }
        }
        if (delayed.length > 0) {
            userInteractionHandler(delayed);
        }
    }

    return {
        run
    }
})();
